import { useLocation, useParams } from "react-router-dom";
import { PageHeader } from "@/components/ui/page-header";
import { LinkTabs } from "@/components/ui/link-tabs";
import { useApp } from "@/hooks/use-app-context";
import { ThemeCreatePanel } from "@/pages/admin/Config/theme-config/ThemeCreatePanel";
import { useExcelFiles } from "./use-excel-files";
import { ExcelFilesHeader } from "./ExcelFilesHeader";
import { ExcelFilesFilters } from "./ExcelFilesFilters";
import { ExcelFilesTable } from "./ExcelFilesTable";
import { ExcelFilesSkeleton } from "./ExcelFilesSkeleton";
import { ExcelFileImportDialog } from "./ExcelFileImportDialog";
import { ExcelFilesError } from "./ExcelFilesError";
import { useExcelImport } from "./use-excel-import";
import { ThemeImportsTab } from "./ThemeImportsTab";
import { themesTabFrom, themesTabHref } from "./themes-tabs";

/**
 * "Themes and phrases": the one place where themes are listed, added (by hand
 * or from Excel) and where phrase files are imported. The former
 * Configuration > Theme page redirects to the "Add a theme" tab.
 */
export default function ExcelFiles() {
  const { t, language } = useApp();
  const { lang = language } = useParams<{ lang: string }>();
  const { search } = useLocation();
  const copy = t.admin.excelFiles;
  const activeView = themesTabFrom(new URLSearchParams(search));

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <PageHeader title={copy.workspaceTitle} description={copy.workspaceDescription} className="border-b-0 pb-0" />
      <LinkTabs
        label={copy.workspaceTitle}
        tabs={[
          { key: "themes", label: copy.themeImportsTab, href: themesTabHref(lang, "themes"), icon: "bookOpen" },
          { key: "new", label: copy.newThemeTab, href: themesTabHref(lang, "new"), icon: "plus" },
          { key: "phrases", label: copy.phraseFilesTab, href: themesTabHref(lang, "phrases"), icon: "fileText" },
        ]}
      />
      {activeView === "themes" ? (
        <ThemeImportsTab language={language} />
      ) : activeView === "new" ? (
        <ThemeCreatePanel />
      ) : (
        <PhraseExcelFiles />
      )}
    </div>
  );
}

function PhraseExcelFiles() {
  const { t } = useApp();
  const adminT = t.admin;
  const x = useExcelFiles();
  const importer = useExcelImport();

  if (x.isLoading) {
    return <ExcelFilesSkeleton />;
  }
  if (x.isError) {
    return <ExcelFilesError message={adminT.excelFiles.loadError} retryLabel={adminT.excelFiles.retry} onRetry={x.refresh} />;
  }

  return (
    <div className="flex w-full flex-col gap-8 pt-7">
      <ExcelFilesHeader
        title={adminT.excelFiles.title}
        description={adminT.excelFiles.subtitle}
        totalCount={x.counts.total}
        pendingCount={x.counts.pending}
        approvedCount={x.counts.approved}
        totalLabel={adminT.excelFiles.totalImports}
        pendingLabel={adminT.excelFiles.pendingImports}
        approvedLabel={adminT.excelFiles.activeImports}
        importLabel={adminT.excelFiles.importAction}
        refreshLabel={adminT.excelFiles.refresh}
        isLoading={x.isRefreshing}
        onImport={() => importer.setOpen(true)}
        onRefresh={x.refresh}
      />

      <ExcelFilesFilters
        cancelLabel={t.cancel}
        filterByLanguageLabel={adminT.excelFiles.filterByLanguage}
        allLanguagesLabel={adminT.excelFiles.allLanguages}
        filterByStatusLabel={adminT.excelFiles.filterByStatus}
        allStatusesLabel={adminT.excelFiles.allStatuses}
        pendingLabel={adminT.excelFiles.pending}
        approvedLabel={adminT.excelFiles.approved}
        rejectedLabel={adminT.excelFiles.rejected}
        deleteAllLabel={adminT.excelFiles.deleteAll}
        confirmTitleLabel={adminT.excelFiles.confirmTitle}
        deleteAllDescriptionLabel={adminT.excelFiles.deleteAllDescription}
        confirmLabel={adminT.excelFiles.confirm}
        selectedLang={x.selectedLang}
        selectedStatus={x.selectedStatus}
        uniqueLangs={x.uniqueLangs}
        onLangChange={x.setSelectedLang}
        onStatusChange={x.setSelectedStatus}
        onDeleteAll={() => x.deleteAllByLangMutation.mutate(x.selectedLang)}
        isDeletingAll={x.deleteAllByLangMutation.isPending}
      />

      <ExcelFilesTable
        files={x.files}
        isLoading={x.isLoading}
        page={x.page}
        totalPages={x.totalPages}
        total={x.total}
        labels={{
          phrases: adminT.excelPhrases.action,
          fileUnknown: adminT.excelFiles.fileUnknown,
          loading: adminT.excelFiles.loading,
          noFiles: adminT.excelFiles.noFiles,
          updateFile: adminT.excelFiles.updateFile,
          excelFile: adminT.excelFiles.excelFile,
          updating: adminT.excelFiles.updating,
          update: adminT.excelFiles.update,
          deleteFileTitle: adminT.excelFiles.deleteFileTitle,
          deleteFileDescription: adminT.excelFiles.deleteFileDescription,
          delete: adminT.users.delete,
          cancel: t.cancel,
          colFile: adminT.excelFiles.colFile,
          colLanguage: adminT.excelFiles.colLanguage,
          colAuthor: adminT.excelFiles.colAuthor,
          colDate: adminT.excelFiles.colDate,
          colActions: adminT.excelFiles.colActions,
          status: adminT.excelFiles.status,
          pending: adminT.excelFiles.pending,
          approved: adminT.excelFiles.approved,
          rejected: adminT.excelFiles.rejected,
          approve: adminT.excelFiles.approve,
          reject: adminT.excelFiles.reject,
          rejectPrompt: adminT.excelFiles.rejectPrompt,
          preview: adminT.excelFiles.actions,
          previewLoading: adminT.excelFiles.loading,
          previewError: adminT.excelFiles.downloadError,
          previewEmpty: adminT.excelFiles.noFiles,
          previewSummary: adminT.excelFiles.previewSummary,
        }}
        updatingFileId={x.updatingFileId}
        selectedFile={x.selectedFile}
        isUpdateDialogOpen={x.isUpdateDialogOpen}
        isDownloading={x.downloadMutation.isPending}
        isUpdating={x.updateMutation.isPending}
        deletingFileId={null}
        isSuperAdmin={x.isSuperAdmin}
        isApproving={x.approveMutation?.isPending ?? false}
        isRejecting={x.rejectMutation?.isPending ?? false}
        onDownload={(id) => x.downloadMutation.mutate(id)}
        onOpenUpdate={x.openUpdateDialog}
        onCloseUpdate={x.closeUpdateDialog}
        onFileSelect={x.setSelectedFile}
        onUpdateSubmit={x.handleUpdateSubmit}
        onDelete={(id) => x.deleteMutation.mutate(id)}
        onApprove={(id) => x.approveMutation?.mutate(id)}
        onReject={(id, reason) => x.rejectMutation?.mutate({ id, reason })}
        onPage={x.setPage}
      />

      <ExcelFileImportDialog
        open={importer.isOpen}
        onOpenChange={importer.setOpen}
        languages={importer.languages}
        language={importer.language}
        file={importer.file}
        copy={{
          title: adminT.excelFiles.importTitle,
          description: adminT.excelFiles.importDescription,
          language: adminT.excelFiles.language,
          selectLanguage: adminT.excelFiles.selectLanguage,
          file: adminT.excelFiles.excelFile,
          noLanguages: adminT.excelFiles.noLanguages,
          fileRequirements: adminT.excelFiles.fileRequirements,
          invalidFile: adminT.excelFiles.invalidFile,
          cancel: t.cancel,
          submit: adminT.excelFiles.importSubmit,
          submitting: adminT.excelFiles.importing,
        }}
        isReady={importer.isReady}
        isSubmitting={importer.isSubmitting}
        onLanguageChange={importer.setLanguage}
        onFileChange={importer.setFile}
        onSubmit={importer.submit}
      />
    </div>
  );
}
